import { ActionButton, Callout, DirectionalHint, Icon, Stack } from "@fluentui/react";
import { Component } from "react";
import { appTheme, cn } from "../../theme";
import { getSiteType, SiteType, siteTypes } from "../../site-data";
import { delayFocus } from "../../util";
import { BuildType } from "../../components/BuildType/BuildType";
import { store } from "../../local-storage";
import { App } from "../../App";
import { CalloutMsg } from "../../components/CalloutMsg";
import { applyTax, isTypeValid2, SysMap2 } from "../../system-model2";
import { TierPoint } from "../../components/TierPoints";
import { EconomyBlock } from "../../components/EconomyBlock";

interface ViewEditBuildTypeProps {
  buildType: string;
  sysMap?: SysMap2;
  dim?: boolean;
  asAddBtn?: boolean;
  highlightAdd?: boolean;
  onChange: (buildType: string) => void;
}

interface ViewEditBuildTypeState {
  dropDown: boolean;
  location: string;
  showTable: boolean;
  alreadyTaxed: boolean;
}

export class ViewEditBuildType extends Component<ViewEditBuildTypeProps, ViewEditBuildTypeState> {
  private mouseInside: boolean = false;

  constructor(props: ViewEditBuildTypeProps) {
    super(props);

    // match the current site's location where if possible
    let defaultLocation = store.viewEditBuiltTypeTab;
    const type = getSiteType(props.buildType, true);
    const isOrbital = props.buildType && type?.orbital;
    if (typeof isOrbital === 'boolean') {
      defaultLocation = isOrbital ? 'orbital' : 'surface';
    }

    this.state = {
      dropDown: false,
      location: defaultLocation,
      showTable: false,
      alreadyTaxed: !!type && type.tier > 1,
    };
  }

  componentDidUpdate(prevProps: Readonly<ViewEditBuildTypeProps>, prevState: Readonly<ViewEditBuildTypeState>, snapshot?: any): void {
    if (!this.state.dropDown || prevState.dropDown) {
      this.mouseInside = false;
      App.resumePageScroll();
    }
  }

  componentWillUnmount(): void {
    App.resumePageScroll();
  }

  render() {
    const { dropDown, location, showTable } = this.state;

    const id = this.props.asAddBtn ? 'add-build-btn' : Date.now().toString();

    const validTypes = siteTypes
      .filter(t => t.tier > 0) // remove unknown types
      .filter(type => location === 'both' || location === (type.orbital ? 'orbital' : 'surface'));

    const displayName2 = getSiteType(this.props.buildType, true)?.displayName2 ?? '?';

    return <div style={{ minWidth: this.props.asAddBtn ? undefined : 290 }}>

      <ActionButton
        id={`bt-${id}`}
        className={cn.bBox}
        onClick={(ev) => {
          ev.preventDefault();
          this.setState({ dropDown: !dropDown, showTable: false, });
        }}
        style={{
          color: this.props.dim ? 'grey' : this.props.highlightAdd ? appTheme.palette.yellowDark : undefined,
          border: this.props.highlightAdd ? '1px solid ' + appTheme.palette.yellowDark : undefined
        }}
        iconProps={{
          iconName: this.props.asAddBtn ? 'Add' : undefined,
          style: { color: this.props.highlightAdd ? appTheme.palette.yellowDark : undefined }
        }}
      >
        {!this.props.asAddBtn && <>
          {displayName2} ({this.props.buildType || '?'})
          <Icon className='icon-inline' iconName={dropDown ? 'CaretSolidRight' : 'CaretSolidDown'} style={{ marginLeft: 4, fontSize: 10, color: 'grey' }} />
        </>}
        {this.props.asAddBtn && <>Add</>}
      </ActionButton>

      {dropDown && <Callout
        target={`#bt-${id}`}
        setInitialFocus
        isBeakVisible={false}
        role="dialog"
        directionalHint={DirectionalHint.rightTopEdge}
        calloutWidth={380}
        style={{
          border: '1px solid ' + appTheme.palette.themePrimary,
          padding: 0,
        }}
        preventDismissOnEvent={(ev) => {
          if (this.mouseInside) {
            ev.preventDefault();
          }
          return this.mouseInside;
        }}
        onDismiss={() => {
          this.setState({ dropDown: false });
        }}
      >
        <div
          className='build-type'
          style={{
            position: 'relative',
            height: window.innerHeight * 0.6,
          }}
          onMouseEnter={() => {
            this.mouseInside = true;
            App.suspendPageScroll();
          }}
          onMouseLeave={() => {
            this.mouseInside = false;
            App.resumePageScroll();
          }}
        >
          <Stack
            horizontal
            tokens={{ childrenGap: 8 }}
            style={{
              backgroundColor: appTheme.palette.white,
              top: 0,
              paddingTop: 5,
              paddingLeft: 5,
            }}
          >
            {this.renderLocationButton('both')}
            {this.renderLocationButton('orbital')}
            {this.renderLocationButton('surface')}
            <ActionButton
              iconProps={{ iconName: 'ViewListGroup' }}
              text='Table'
              className={cn.bBox}
              onClick={() => {
                this.setState({ dropDown: false, showTable: true });
              }}
            >
            </ActionButton>
          </Stack>

          <div style={{
            overflowY: 'scroll',
            padding: 10,
            position: 'absolute',
            left: 0,
            right: 0,
            top: 30,
            bottom: 0,
          }}>
            <div className='list'>
              {validTypes.map(this.renderTypeRow)}
            </div>
          </div>
        </div>
      </Callout>}

      {showTable && <BuildType
        buildType={this.props.buildType}
        tableOnly={true}
        sysMap2={this.props.sysMap}
        onChange={(newValue) => {
          this.props.onChange(newValue);
          this.setState({ showTable: false });
        }}
      />}

    </div>;
  }

  renderLocationButton(targetLocation: string) {
    return <>
      <ActionButton
        text={targetLocation}
        style={{
          textTransform: 'capitalize',
          backgroundColor: this.state.location === targetLocation ? appTheme.palette.themeLight : undefined,
        }}
        className={cn.bBox}
        onClick={() => {
          this.setState({ dropDown: false });
          setTimeout(() => {
            this.setState({
              location: targetLocation,
              dropDown: true,
            });
            store.viewEditBuiltTypeTab = targetLocation;
          }, 5);
        }}
      />
    </>;
  }

  renderTypeRow = (type: SiteType, idx: number) => {
    const selection = this.props.buildType;
    const isCurrentSelection = selection && (type.subTypes.includes(selection) || type.altTypes?.includes(selection) || type.subTypes[0] === selection.slice(0, -1));

    const id = `bbt-${type.subTypes[0]}`;
    if (isCurrentSelection) {
      delayFocus(id);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element?.offsetParent && element.offsetParent.clientHeight < element.offsetTop) {
          element.offsetParent.scrollTo(0, element.offsetTop - 2);
        }
      }, 10);
    }

    const { isValid, msg, unlocks } = isTypeValid2(this.props.sysMap, type, getSiteType(this.props.buildType, true));
    let msgElement = <div>
      {msg && <Stack horizontal verticalAlign='center'>
        <Icon iconName={isValid ? 'Accept' : 'ChromeClose'} style={{ marginRight: 4, fontWeight: 'bolder', color: isValid ? appTheme.palette.greenLight : appTheme.palette.red }} />
        <span>{msg}</span>
      </Stack>}
      {unlocks && <>
        {unlocks.map(t => {
          return <div key={`${id}-${t}}`}>
            <Icon iconName={t.startsWith('System') ? 'UnlockSolid' : 'Unlock'} style={{ marginRight: 4 }} />
            <span>{t}</span>
          </div>;
        })}
      </>}
    </div>;

    let needCount = type.needs.count;
    if (type.buildClass === 'starport' && type.tier > 1) {
      let taxCount = this.props.sysMap?.taxCount ?? 0;
      if (!this.state.alreadyTaxed && type.needs.tier > 1) { taxCount++; }
      needCount = applyTax(type.needs.tier, type.needs.count, taxCount);
    }

    return <div
      key={id}
      id={id}
      style={{
        backgroundColor: idx % 2 ? appTheme.palette.neutralLighter : undefined,
        borderLeft: isCurrentSelection ? `4px solid ${appTheme.palette.accent}` : undefined,
        fontWeight: isCurrentSelection ? 'bold' : undefined,
        paddingLeft: isCurrentSelection ? 4 : undefined,
      }}

      onClick={(ev) => {
        if (!ev.defaultPrevented) {
          if (!type.subTypes.includes(this.props.buildType)) {
            this.props.onChange(type.subTypes[0] + (type.subTypes.length === 1 ? '' : '?'));
          }
          this.setState({ dropDown: false });
        }
      }}
    >
      <div style={{ float: 'right', fontSize: 12 }}>
        {type.needs.count > 0 && <TierPoint tier={type.needs.tier} count={-needCount} titlePrefix='Needs' />}
        {type.gives.count > 0 && <TierPoint tier={type.gives.tier} count={type.gives.count} titlePrefix='Gives' />}
      </div>

      <Stack horizontal verticalAlign='center' style={{ color: appTheme.palette.themePrimary }}>
        <span>{type.displayName2}</span>
        {(msg || unlocks) && <span style={{ marginLeft: 8, marginTop: 4, }}>
          <CalloutMsg
            directionalHint={DirectionalHint.rightCenter}
            msg={msgElement}
            iconName={isValid ? undefined : 'Warning'}
            iconStyle={{
              fontSize: 12,
              fontWeight: isValid ? undefined : 'bold',
              color: isValid ? appTheme.palette.black : appTheme.palette.yellowDark,
            }} />
        </span>}
      </Stack>

      <Stack horizontal wrap tokens={{ childrenGap: 0 }} style={{ marginLeft: 8, fontSize: 12 }} verticalAlign='center'>
        <EconomyBlock economy={type.inf} size='10px' />
        <span>&nbsp;</span>
        {type.subTypes.map(st => (<ActionButton
          key={`st-${st}`}
          id={`st-${st}`}
          className={cn.bBold}
          style={{
            color: selection === st ? appTheme.palette.black : undefined,
            backgroundColor: selection === st ? appTheme.palette.neutralLighter : undefined,
            fontSize: 12,
            fontWeight: selection === st ? 'bold' : undefined,
            height: 16,
            padding: '0 0 2px 0',
            margin: 1,
            border: `1px solid ${selection === st ? appTheme.palette.themePrimary : 'grey'}`,
          }}
          onClick={(ev) => {
            ev.preventDefault();
            this.props.onChange(st);
            this.setState({ dropDown: false });
          }}
        >
          {st.replace('_i', '').replace('_e', '')}
        </ActionButton>))}
      </Stack>
    </div>;
  }
}